import type { CommandDef } from './types';

/**
 * Module-level registry of every slash command available to the
 * `GlobalMapSearch` palette. Keyed by `id` for O(1) lookup; the
 * insertion order is preserved by the underlying `Map` so the
 * default catalog ordering matches the registration order in
 * `defs/index.ts`.
 *
 * Side-effect populated: each `defs/<name>.ts` file calls
 * `registerCommand()` at import time, and the `defs/index.ts`
 * barrel pulls them all in. Importing the barrel from the host
 * (`useCommandMode.ts`) is what guarantees the registry is ready
 * before the first render.
 */
const REGISTRY = new Map<string, CommandDef>();

/**
 * Add a command to the registry. Re-registering the same id is a
 * developer error (typically a copy-paste during scaffolding) — we
 * `console.error` rather than throw so a bad def in one branch can't
 * crash the whole search bar at boot.
 */
export function registerCommand(def: CommandDef): void {
  if (REGISTRY.has(def.id)) {
    // eslint-disable-next-line no-console
    console.error(
      `[GlobalMapSearch/command] duplicate command id "${def.id}" — second registration ignored`,
    );
    return;
  }
  REGISTRY.set(def.id, def);
}

/**
 * Return all registered commands in insertion order. The matcher and
 * palette are responsible for any further filtering (capability gate,
 * fuzzy match, etc.) — the registry itself is intentionally stateless
 * about who is allowed to see what.
 */
export function getAllCommands(): CommandDef[] {
  return Array.from(REGISTRY.values());
}

/**
 * Lookup a single command by canonical id (not alias). Returns
 * `undefined` for unknown ids — callers decide how to surface that
 * (`useCommandMode.execute` reports `not_found` to the host).
 */
export function getCommandById(id: string): CommandDef | undefined {
  return REGISTRY.get(id);
}

/**
 * Internal: clear the registry. Exists only for unit tests, which
 * need a fresh registry per test case to avoid order-of-import
 * coupling between test files. Not exported from the package barrel.
 */
export function _resetRegistryForTests(): void {
  REGISTRY.clear();
}
